import React, { useState, useCallback, useEffect } from 'react';
import { Settings, Download, RefreshCw } from 'lucide-react';
import { InputPanel } from './InputPanel';
import { KPIDashboard } from './KPIDashboard';
import { Charts } from './Charts';
import { ReportGenerator } from './ReportGenerator';
import { QueueingSystemParams, QueueingMetrics } from '../types';
import { QueueingCalculator } from '../utils/queueingCalculations';

interface QueueingAnalysisTabProps {
  isCalculating: boolean;
  setIsCalculating: (calculating: boolean) => void;
}

export const QueueingAnalysisTab: React.FC<QueueingAnalysisTabProps> = ({ 
  isCalculating, 
  setIsCalculating 
}) => {
  const [params, setParams] = useState<QueueingSystemParams>({
    arrivalRate: 50,
    serviceRate: 60,
    arrivalCV: 1,
    serviceCV: 1,
    servers: 1
  });
  
  const [metrics, setMetrics] = useState<QueueingMetrics | null>(null);

  const calculateMetrics = useCallback(async () => {
    setIsCalculating(true);
    
    // Add small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      const result = QueueingCalculator.calculateMetrics(params);
      setMetrics(result);
    } catch (error) {
      console.error('Calculation error:', error);
      setMetrics({
        utilization: 0,
        processTime: 0,
        queueTime: 0,
        cycleTime: 0,
        wip: 0,
        wipQueue: 0,
        throughput: 0,
        error: 'Calculation failed. Please check your parameters.'
      });
    } finally {
      setIsCalculating(false);
    }
  }, [params, setIsCalculating]);

  const handleParamsChange = useCallback((newParams: QueueingSystemParams) => {
    setParams(newParams);
  }, []);

  const handleReset = useCallback(() => {
    setParams({
      arrivalRate: 50,
      serviceRate: 60,
      arrivalCV: 1,
      serviceCV: 1,
      servers: 1
    });
  }, []);

  const handleExport = useCallback(() => {
    if (!metrics) return;
    
    const report = {
      timestamp: new Date().toISOString(),
      parameters: params,
      metrics: metrics,
      analysis: {
        systemType: params.servers === 1 ? 'Single Server' : `${params.servers} Servers`,
        stabilityStatus: metrics.utilization < 1 ? 'Stable' : 'Unstable',
        performanceLevel: metrics.utilization < 0.7 ? 'Good' : metrics.utilization < 0.9 ? 'Moderate' : 'Poor',
        recommendations: metrics.utilization > 0.85 ? ['Consider adding capacity', 'Reduce variability', 'Implement load balancing'] : ['System performing well']
      }
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `queueing-analysis-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [metrics, params]);

  // Auto-calculate on mount and params change
  useEffect(() => {
    calculateMetrics();
  }, [calculateMetrics]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      {/* Input Panel - Left Side */}
      <div className="lg:col-span-1">
        <InputPanel
          params={params}
          onParamsChange={handleParamsChange}
          onCalculate={calculateMetrics}
          isCalculating={isCalculating}
        />
      </div>

      {/* Results Panel - Right Side */}
      <div className="lg:col-span-3 space-y-8">
        {/* KPI Dashboard */}
        {metrics && (
          <div className="slide-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-200 flex items-center">
                <div className="w-1 h-6 bg-primary-500 rounded-full mr-3"></div>
                Performance Metrics
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleReset}
                  disabled={isCalculating}
                  className="btn-secondary p-2"
                  title="Reset to default values"
                >
                  <RefreshCw className={`w-4 h-4 ${isCalculating ? 'animate-spin' : ''}`} />
                </button>

                <button
                  onClick={handleExport}
                  disabled={isCalculating}
                  className="btn-secondary p-2"
                  title="Export analysis report"
                >
                  <Download className="w-4 h-4" />
                </button>

                <button className="btn-secondary p-2" title="Settings">
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </div>
            <KPIDashboard metrics={metrics} params={params} />
          </div>
        )}

        {/* Charts */}
        <div className="slide-in">
          <h2 className="text-xl font-semibold text-gray-200 mb-6 flex items-center">
            <div className="w-1 h-6 bg-accent-500 rounded-full mr-3"></div>
            System Analysis
          </h2>
          <Charts params={params} />
        </div>

        {/* Report Generator */}
        {metrics && (
          <div className="slide-in">
            <h2 className="text-xl font-semibold text-gray-200 mb-6 flex items-center">
              <div className="w-1 h-6 bg-green-500 rounded-full mr-3"></div>
              Analysis Report
            </h2>
            <ReportGenerator params={params} metrics={metrics} />
          </div>
        )}
      </div>
    </div>
  );
};